/**
 * Token Manager
 *
 * Module for managing the Shopify App Bridge token getter function.
 * Provides a centralized way to access authentication tokens without polluting
 * the global namespace.
 */

let tokenGetterInstance = null;

/**
 * Wait for token manager to be initialized
 *
 * @param {number} timeout - Maximum time to wait in milliseconds
 * @returns {Promise<void>} Promise that resolves when initialized or rejects on timeout
 */
async function waitForInitialization(timeout) {
  const start = Date.now();
  const checkInterval = 50; // Check every 50ms

  while (!tokenGetterInstance) {
    if (Date.now() - start > timeout) {
      throw new Error(
        `Token manager initialization timeout after ${timeout}ms. ` +
        "Ensure AppBridgeAuthInjector is rendered before making API calls."
      );
    }
    await new Promise((resolve) => setTimeout(resolve, checkInterval));
  }
}

export const tokenManager = {
  /**
   * Set the token getter function
   *
   * Should be called once during app initialization by AppBridgeAuthInjector.
   *
   * @param {Function} getter - Function that returns the current Shopify session token
   */
  setTokenGetter(getter) {
    tokenGetterInstance = getter;

    if (process.env.NODE_ENV === 'development') {
      console.log("[TokenManager] Token getter initialized");
    }
  },

  /**
   * Get the current authentication token
   *
   * If the token manager is not yet initialized, this will wait up to 5 seconds
   * for initialization to complete. This handles race conditions where API calls
   * might occur before AppBridgeAuthInjector has mounted.
   *
   * @returns {Promise<string>} Promise that resolves to the current session token
   * @throws Error if token getter not initialized within timeout period
   */
  async getToken() {
    // If not initialized, wait for initialization with timeout
    if (!tokenGetterInstance) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("[TokenManager] Token getter not yet initialized, waiting up to 5 seconds...");
      }

      await waitForInitialization(5000);
    }

    // Guard after waiting
    if (!tokenGetterInstance) {
      throw new Error("Token getter not initialized after waiting");
    }

    return await tokenGetterInstance();
  },

  /**
   * Clear the token getter
   *
   * Should be called on logout or when switching accounts.
   * After calling this, subsequent getToken() calls will fail until
   * setTokenGetter() is called again.
   */
  clear() {
    tokenGetterInstance = null;

    if (process.env.NODE_ENV === 'development') {
      console.log("[TokenManager] Token getter cleared");
    }
  },

  /**
   * Check if token manager is initialized
   *
   * @returns {boolean} true if token getter has been set, false otherwise
   */
  isInitialized() {
    return tokenGetterInstance !== null;
  },
};
